import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import LazyChart from "./LazyChart";
import { useProgressiveOverviewData } from "./useProgressiveOverviewData";
import { CUSTOM_CHART_CONFIGS } from "./chartConfigs";
import { useSettings } from "../../../context/SettingsContext";
import NormalArrowFunction from "../../common/NormalArrowFunction.jsx";

const STORAGE_KEY = "appSettings";

export default function DashboardAnalyticsChart({
  vehiclesData,
  routesData,
  driversData,
  stopsData,
  sitesData,
  usersData,
  selectedDateRange = "today",
  customDateRange = null,
}) {
  const { t } = useTranslation();
  const { settings, loading: settingsLoading } = useSettings();
  const [selectedCharts, setSelectedCharts] = useState([]);
  const [userPreferredCharts, setUserPreferredCharts] = useState([]);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [shouldFetchData, setShouldFetchData] = useState(false);

  const {
    chartDataStates,
    loadingStates,
    error: overviewError,
  } = useProgressiveOverviewData(
    shouldFetchData ? selectedDateRange : null,
    customDateRange,
  );

  useEffect(() => {
    if (preferencesLoaded && selectedCharts.length > 0) {
      setShouldFetchData(true);
    }
  }, [preferencesLoaded, selectedCharts]);

  useEffect(() => {
    if (settingsLoading) {
      return;
    }

    const fallbackSettings = (() => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
      } catch (error) {
        console.error("Error reading app settings from localStorage:", error);
        return {};
      }
    })();

    const selectedGraphs = Array.isArray(settings?.selectedGraphs)
      ? settings.selectedGraphs
      : Array.isArray(fallbackSettings?.selectedGraphs)
        ? fallbackSettings.selectedGraphs
        : [];

    const validChartIds = new Set(CUSTOM_CHART_CONFIGS.map((chart) => chart.id));
    const preferredChartIds = selectedGraphs
      .map((graph) => graph?.chartId)
      .filter((chartId) => chartId && validChartIds.has(chartId));

    setUserPreferredCharts(preferredChartIds);
    setSelectedCharts(preferredChartIds);
    setPreferencesLoaded(true);
  }, [settings, settingsLoading]);

  // Process charts with their data states
  const processedCharts = CUSTOM_CHART_CONFIGS.map((chartConfig) => {
    const dataKey = `${chartConfig.reportType}_${chartConfig.category}`;
    const chartState = chartDataStates[dataKey] || {
      loading: true,
      data: null,
      error: null,
    };

    return {
      ...chartConfig,
      chartState,
    };
  });

  const availableCharts = preferencesLoaded
    ? processedCharts.filter((chart) => userPreferredCharts.includes(chart.id))
    : [];

  const handleChartSelection = (event) => {
    const value = event.target.value;
    setSelectedCharts(typeof value === "string" ? value.split(",") : value);
  };

  // Filter charts based on selection - only show selected charts
  const displayedCharts =
    selectedCharts.length > 0
      ? availableCharts.filter((chart) => selectedCharts.includes(chart.id))
      : availableCharts;

  if (settingsLoading || !preferencesLoaded) {
    return (
      <Box sx={{ width: "100%", py: 3 }}>
        <Typography color="text.secondary">
          {t(
            "loadingChartPreferences",
            "Loading chart preferences...",
          )}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      {overviewError && (
        <Box sx={{ mb: 2, p: 2, bgcolor: "#ffebee", borderRadius: 1 }}>
          <Typography color="error">{overviewError}</Typography>
        </Box>
      )}

      {/* Chart Selection Dropdown */}
      <Box
        sx={{
          mb: 3,
          mt: 2,
          display: "flex",
          justifyContent: "flex-start",
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          flexWrap: "wrap",
          gap: { xs: 1, sm: 2 },
          width: "100%",
          minWidth: 0,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            display: "flex",
            alignItems: "center",
            fontFamily: "Roboto",
            fontWeight: 500,
            fontSize: { xs: "14px", sm: "16px" },
            lineHeight: 1.3,
          }}
        >
          {t("chartDisplayOptions", "Chart Display Options")}
        </Typography>
        <FormControl
          size="small"
          sx={{
            minWidth: { xs: 0, sm: 250 },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <Select
            multiple
            IconComponent={NormalArrowFunction}
            sx={{
              border: "1px solid #C8CBD0",
              width: { xs: "100%", sm: 280 },
              height: 43,
              borderRadius: "12px",
              paddingTop: "12px",
              paddingRight: { xs: "12px", sm: "24px" },
              paddingBottom: "12px",
              paddingLeft: { xs: "12px", sm: "24px" },
              "&": {
                cursor: "pointer",
                userSelect: "none",
              },
              "& .MuiSelect-select": {
                cursor: "pointer",
                userSelect: "none",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
              "& .MuiSelect-select *": {
                cursor: "pointer",
                userSelect: "none",
              },
            }}
            displayEmpty
            value={selectedCharts}
            onChange={handleChartSelection}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return (
                  <em>
                    {t("selectChartsToDisplay", "Select Charts to Display")}
                  </em>
                );
              }
              return t("chartsSelected", {
                count: selected.length,
                defaultValue: "{{count}} charts selected",
              });
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 300,
                  "& .MuiMenuItem-root": {
                    fontSize: "14px",
                  },
                },
              },
            }}
          >
            <MenuItem disabled value="">
              <em>{t("selectChartsToDisplay", "Select Charts to Display")}</em>
            </MenuItem>
            {availableCharts.map((chart) => (
              <MenuItem key={chart.id} value={chart.id}>
                <Checkbox
                  checked={selectedCharts.includes(chart.id)}
                  sx={{
                    color: "#20A8E0",
                    "&.Mui-checked": {
                      color: "#20A8E0",
                    },
                  }}
                />
                <ListItemText primary={t(chart.title, chart.title)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Charts Grid */}
      <Grid container spacing={2} justifyContent="center">
        {displayedCharts
          .filter(
            (chartConfig) =>
              chartConfig.id !== "trip_gantt_timeline" &&
              chartConfig.id !== "overspeed_timeline"
          )
          .map((chartConfig, chartIndex) => (
            <Grid item xs={12} md={6} lg={4} key={`chart-${chartIndex}`}>
              <Box
                sx={{
                  p: 1.5,
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  backgroundColor: "white",
                  minHeight: "320px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 3px 6px rgba(0,0,0,0.15)",
                  },
                }}
              >
                <LazyChart
                  chartConfig={{
                    ...chartConfig,
                    width: "100%",
                    height: 260,
                  }}
                  chartState={chartConfig.chartState}
                  loadingState={
                    loadingStates[
                      `${chartConfig.reportType}_${chartConfig.category}`
                    ]
                  }
                  vehiclesData={vehiclesData}
                  selectedDateRange={selectedDateRange}
                />
              </Box>
            </Grid>
          ))}
      </Grid>

      {/* Timeline Charts - Full Width */}
      {displayedCharts
        .filter(
          (chartConfig) =>
            chartConfig.id === "trip_gantt_timeline" ||
            chartConfig.id === "overspeed_timeline"
        )
        .map((chartConfig, chartIndex) => (
          <Box
            key={`timeline-${chartIndex}`}
            sx={{
              mt: 2,
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              backgroundColor: "white",
              minHeight: "400px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
              },
            }}
          >
            <LazyChart
              chartConfig={{
                ...chartConfig,
                width: "100%",
                height: 350,
              }}
              chartState={chartConfig.chartState}
              vehiclesData={vehiclesData}
              selectedDateRange={selectedDateRange}
              isTimelineChart={true}
            />
          </Box>
        ))}

      {displayedCharts.length === 0 && userPreferredCharts.length > 0 && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            {t(
              "noChartsSelectedMessage",
              "No charts selected. Please select charts to display.",
            )}
          </Typography>
        </Box>
      )}

      {availableCharts.length === 0 && preferencesLoaded && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            {t(
              "noChartsConfiguredMessage",
              "No charts configured. Please configure charts in App Settings.",
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
